import React, { useState, useEffect, useLayoutEffect } from "react";
import Modal from "components/UI/Modal/Modal";
import { InsertModal, UpdateModal, DeleteModal } from "./Actions";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import { HeaderButton } from "components";
import Add from "@mui/icons-material/Add";
import Cached from "@mui/icons-material/Cached";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { Buttons } from "components";
import { ACTIONS } from "consts/Actions";
import { agrupamientoTitles } from "consts/titulos-de-tablas";
import CeldaDeChips from "components/UI/Grid/CeldaDeChips";
import styles from "styles/pages/Pages.module.scss";
import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import XGrid from "components/UI/Grid/XGrid";
import { RNHAgrupamientoGet } from "services/services-mia/programa-salud/personal/RNHAgrupamientoGet";

/**
 * Los nombres de las funciones que integran un agrupamiento.
 *
 * NO se cargan desde acá: se le asignan a cada función desde su propia vista
 * (`funcionesTitles` tiene la columna "Agrupamiento"). Acá solo se muestran.
 *
 * El back las manda anidadas, como array de objetos y solo con el nombre:
 *
 *   "rnhAgrupamientoFunciones": [{ "rnhFuncionNombre": "Mucama" }, ...]
 *
 * Se devuelve un ARRAY y no un string unido por comas: la celda necesita los nombres
 * sueltos para decidir cuantos entran enteros. El `?? []` cubre al agrupamiento que
 * todavía no tiene ninguna, donde el back puede no mandar la clave.
 */
const nombresDeFunciones = (funciones) =>
  (funciones ?? []).map((f) => f?.rnhFuncionNombre).filter(Boolean);

/**
 * El renderCell se inyecta acá y no en `titulos-de-tablas` para que ese archivo siga
 * siendo solo datos, sin importar componentes. Es una constante de modulo: recalcularlo
 * en cada render le cambiaria la identidad a las columnas al grid.
 */
const titles = agrupamientoTitles.map((t) =>
  t.field === "funciones"
    ? {
        ...t,
        renderCell: ({ value }) => (
          <CeldaDeChips items={value} vacio="Sin funciones" />
        ),
      }
    : t
);

export default function Agrupamientos() {
  const credentials = useRecoilValue(userCredentials);
  const setRoute = useSetRecoilState(routeInfo);
  const [modalDetails, setModalDetails] = useState({ isOpen: false });
  const [loading, setLoading] = useState(false);
  const [agrupamientos, setAgrupamientos] = useState([]);
  const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
  const { service } = useService();

  const getAgrupamientos = async () => {
    setAgrupamientos([]);
    setRowSelected({ status: false, row: {} });
    setLoading(true);

    const respuesta = await RNHAgrupamientoGet(credentials);
    setLoading(false);
    if (!respuesta.ok) return;

    const newRows = respuesta.data
      // Filtra la fila centinela que el back manda cuando no hay registros.
      .filter((data) => data?.rnhAgrupamientoId)
      .map((data) => ({
        id: data.rnhAgrupamientoId,
        cod: data.rnhAgrupamientoCod ?? "",
        nombre: data.rnhAgrupamientoNombre ?? "",
        // El array entero: la celda mide cuantos nombres entran y esconde el resto.
        // El caso vacio lo resuelve la celda, no la fila.
        funciones: nombresDeFunciones(data.rnhAgrupamientoFunciones),
        // No es columna: lo necesitan los modales de modificar y eliminar.
        version: data.rnhAgrupamientoVersion,
      }));

    setAgrupamientos(newRows);
  }

  const closeModal = () => {
    setModalDetails({ isOpen: false });
  };
  const handleClick = ({ procede, title }) => {
    setModalDetails({ procede, title, isOpen: true });
  };

  useEffect(() => {
    getAgrupamientos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useLayoutEffect(() => {
    setRoute({
      wordKey: `Agrupamientos - Programa Salud RN`
    })
  }, [service.servicioNombre, setRoute])

  return (
    <main className={styles.main}>
      <Modal
        size={modalDetails.procede === ACTIONS.DELETE ? 'sm' : 'md'}
        open={modalDetails.isOpen}
        title={modalDetails.title}
        onClose={closeModal}
      >
        {modalDetails.procede === ACTIONS.UPDATE && (
          <UpdateModal
            row={rowSelected.row}
            updateList={getAgrupamientos}
            closeModal={closeModal}
          />
        )}
        {modalDetails.procede === ACTIONS.DELETE && (
          <DeleteModal
            row={rowSelected.row}
            servicio={service}
            updateList={getAgrupamientos}
            closeModal={closeModal}
            setterRowSelected={setRowSelected}
          />
        )}
        {modalDetails.procede === ACTIONS.INSERT && (
          <InsertModal
            servicio={service}
            updateList={getAgrupamientos}
            closeModal={closeModal}
          />
        )}
      </Modal>
      <div className={styles["filters-actions-right"]}>
        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={() => { getAgrupamientos() }}
            disabled={rowSelected.row.id || false}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Nuevo Agrupamiento"}
            handleClick={handleClick}
            data={{
              title: "Nuevo Agrupamiento",
              procede: ACTIONS.INSERT
            }}
            disabled={!puedeEditarServicio(service) || rowSelected.row.id}
          >
            <Add />
          </HeaderButton>
          <HeaderButton
            text={"Modificar Agrupamiento"}
            handleClick={handleClick}
            data={{
              title: "Modificar Agrupamiento",
              procede: ACTIONS.UPDATE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <EditOutlined />
          </HeaderButton>
          <HeaderButton
            text={"Eliminar Agrupamiento"}
            handleClick={handleClick}
            data={{
              title: "Eliminar Agrupamiento",
              procede: ACTIONS.DELETE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <DeleteOutlineRoundedIcon />
          </HeaderButton>
        </Buttons>
      </div>
      <div className={styles.container}>
        <XGrid
          rows={agrupamientos}
          titles={titles}
          loading={loading}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
        />
      </div>
    </main>
  );
}
